/** @format */

import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import RevalidatingRacer, {
  type RacerCar,
  StockCarSilhouette,
} from "@/components/RevalidatingRacer";

export const Route = createFileRoute("/dev/revalidating-racer")({
  beforeLoad: () => {
    if (!import.meta.env.DEV) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: RevalidatingRacerPreview,
});

const PRESET_COLORS: Array<{ label: string; value: string }> = [
  { label: "Sky", value: "#38bdf8" },
  { label: "Amber", value: "#fbbf24" },
  { label: "Emerald", value: "#34d399" },
  { label: "Rose", value: "#fb7185" },
  { label: "Violet", value: "#a78bfa" },
  { label: "White", value: "#ffffff" },
];

// Field/palette used when the "Number of cars" control is > 1.
const GRID_COLORS = [
  "#38bdf8",
  "#fbbf24",
  "#34d399",
  "#fb7185",
  "#a78bfa",
  "#f97316",
  "#22d3ee",
  "#f472b6",
];
const GRID_NUMBERS = ["88", "24", "5", "11", "9", "2", "4", "48"];

function RevalidatingRacerPreview() {
  const [active, setActive] = useState(true);
  const [durationMs, setDurationMs] = useState(12_000);
  const [size, setSize] = useState(100);
  const [marginPx, setMarginPx] = useState(2);
  const [direction, setDirection] = useState<"cw" | "ccw">("ccw");
  const [color, setColor] = useState(PRESET_COLORS[0].value);
  const [cornerRadiusAuto, setCornerRadiusAuto] = useState(true);
  const [cornerRadius, setCornerRadius] = useState(40);
  const [simulatingRevalidation, setSimulatingRevalidation] = useState(false);
  const [carCount, setCarCount] = useState(1);

  const cars: RacerCar[] | undefined =
    carCount > 1
      ? Array.from({ length: carCount }, (_, i) => ({
          color: GRID_COLORS[i % GRID_COLORS.length],
          number: GRID_NUMBERS[i % GRID_NUMBERS.length],
        }))
      : undefined;

  // "Simulate revalidation" pulses `active` off after ~1.5 laps, mimicking
  // a real query finishing its background refetch.
  useEffect(() => {
    if (!simulatingRevalidation) return;
    setActive(true);
    const timer = window.setTimeout(() => {
      setActive(false);
      setSimulatingRevalidation(false);
    }, durationMs * 1.5);
    return () => window.clearTimeout(timer);
  }, [simulatingRevalidation, durationMs]);

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-slate-100 mb-2">Revalidating Racer</h1>
      <p className="text-slate-400 mb-6 text-sm">
        A stock car loops the viewport edge while data revalidates. Uses CSS{" "}
        <code className="font-mono text-slate-300">offset-path: inset(...)</code>, so
        the browser handles the rectangular path and corner rotation.
      </p>

      {/* Static model render — for screenshot / design review. Not affected by
          the animation/CCW logic; always the raw SVG at rest. */}
      <div
        className="mb-8 rounded-lg border border-slate-700 bg-slate-950 p-6 grid place-items-center"
        style={{ color }}
      >
        <p className="text-xs text-slate-500 mb-4 self-start">
          Static model (body color tracks the Color control above)
        </p>
        <div
          style={{
            width: 600,
            height: 240,
            maxWidth: "100%",
            display: "grid",
            placeItems: "center",
          }}
        >
          <StockCarSilhouette />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <ControlCard title="Visibility">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setActive(a => !a)}
              className={`rounded px-3 py-1.5 text-sm font-medium ${
                active
                  ? "bg-sky-600 hover:bg-sky-500 text-white"
                  : "bg-slate-700 hover:bg-slate-600 text-slate-200"
              }`}
            >
              {active ? "Visible" : "Hidden"} (toggle)
            </button>
            <button
              type="button"
              onClick={() => setSimulatingRevalidation(true)}
              disabled={simulatingRevalidation}
              className="rounded px-3 py-1.5 text-sm font-medium bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-400 text-white"
            >
              {simulatingRevalidation ? "Simulating…" : "Simulate revalidation"}
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            "Simulate" drives the car for ~1.5 laps then hides it — same shape as a real
            query's background refetch.
          </p>
        </ControlCard>

        <ControlCard title="Direction">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDirection("cw")}
              className={`rounded px-3 py-1.5 text-sm font-medium ${
                direction === "cw"
                  ? "bg-sky-600 text-white"
                  : "bg-slate-700 hover:bg-slate-600 text-slate-200"
              }`}
            >
              Clockwise
            </button>
            <button
              type="button"
              onClick={() => setDirection("ccw")}
              className={`rounded px-3 py-1.5 text-sm font-medium ${
                direction === "ccw"
                  ? "bg-sky-600 text-white"
                  : "bg-slate-700 hover:bg-slate-600 text-slate-200"
              }`}
            >
              Counter-clockwise
            </button>
          </div>
        </ControlCard>

        <ControlCard title={`Duration: ${durationMs} ms / lap`}>
          <input
            type="range"
            aria-label="Lap duration in milliseconds"
            min={2_000}
            max={30_000}
            step={500}
            value={durationMs}
            onChange={e => setDurationMs(Number(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>2 s</span>
            <span>30 s</span>
          </div>
        </ControlCard>

        <ControlCard title={`Length: ${size} px`}>
          <input
            type="range"
            aria-label="Car length in pixels"
            min={40}
            max={200}
            step={2}
            value={size}
            onChange={e => setSize(Number(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>40</span>
            <span>200</span>
          </div>
        </ControlCard>

        <ControlCard title={`Edge margin: ${marginPx} px`}>
          <input
            type="range"
            aria-label="Edge margin in pixels"
            min={0}
            max={48}
            step={1}
            value={marginPx}
            onChange={e => setMarginPx(Number(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>0</span>
            <span>48</span>
          </div>
        </ControlCard>

        <ControlCard title="Color">
          <div className="flex flex-wrap gap-2">
            {PRESET_COLORS.map(preset => (
              <button
                key={preset.value}
                type="button"
                onClick={() => setColor(preset.value)}
                title={preset.label}
                aria-label={preset.label}
                className={`h-8 w-8 rounded-full border-2 transition ${
                  color === preset.value ? "border-white scale-110" : "border-slate-700"
                }`}
                style={{ backgroundColor: preset.value }}
              />
            ))}
          </div>
        </ControlCard>

        <ControlCard title={`Cars on track: ${carCount}`}>
          <input
            type="range"
            aria-label="Number of cars on track"
            min={1}
            max={36}
            step={1}
            value={carCount}
            onChange={e => setCarCount(Number(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>1</span>
            <span>36</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Cars &gt; 1 auto-space evenly around the lap with a rotating color + number
            palette. Car count of 1 uses the Color control above.
          </p>
        </ControlCard>

        <ControlCard
          title={
            cornerRadiusAuto
              ? "Corner radius: auto"
              : `Corner radius: ${cornerRadius} px`
          }
        >
          <label className="flex items-center gap-2 text-sm text-slate-300 mb-3">
            <input
              type="checkbox"
              checked={cornerRadiusAuto}
              onChange={e => setCornerRadiusAuto(e.target.checked)}
            />
            Auto (tightest that keeps car on-screen)
          </label>
          <input
            type="range"
            aria-label="Corner radius in pixels"
            min={0}
            max={200}
            step={1}
            value={cornerRadius}
            onChange={e => setCornerRadius(Number(e.target.value))}
            className="w-full disabled:opacity-40"
            disabled={cornerRadiusAuto}
          />
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>0 (sharp 90°)</span>
            <span>200 (full arc)</span>
          </div>
        </ControlCard>
      </div>

      <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-4 text-sm text-slate-300">
        <p className="font-medium text-slate-200 mb-1">Notes for iteration</p>
        <ul className="list-disc list-inside space-y-1 text-slate-400">
          <li>
            The car respects <code className="font-mono">prefers-reduced-motion</code>
            and hides itself for users who opted out.
          </li>
          <li>
            The path is an <code className="font-mono">inset()</code> rectangle with
            rounded corners matching the margin — tweak{" "}
            <code className="font-mono">round</code> in the component to change the
            corner radius.
          </li>
          <li>
            For production use, wire <code className="font-mono">active</code> to{" "}
            <code className="font-mono">useIsFetching()</code> or a specific query's{" "}
            <code className="font-mono">isFetching &amp;&amp; !isLoading</code> flag
            (i.e. background refetch, not initial load).
          </li>
        </ul>
      </div>

      <RevalidatingRacer
        active={active}
        durationMs={durationMs}
        size={size}
        marginPx={marginPx}
        direction={direction}
        color={color}
        cars={cars}
        cornerRadius={cornerRadiusAuto ? undefined : cornerRadius}
      />
    </div>
  );
}

function ControlCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900 p-4">
      <h2 className="text-sm font-medium text-slate-200 mb-3">{title}</h2>
      {children}
    </div>
  );
}
